import { useEffect, useState, useCallback } from "react";
import { Users, TrendingUp, Activity, Gauge, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

export default function LiveStats() {
  const [stats, setStats] = useState(null);
  const [spinning, setSpinning] = useState(false);

  const fetchStats = useCallback(async (manual = false) => {
    if (manual) setSpinning(true);
    try {
      const res = await api.get("/stats");
      setStats(res.data);
    } catch {} finally {
      if (manual) setTimeout(() => setSpinning(false), 500);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(() => fetchStats(), 10000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  const cards = [
    {
      testid: "stats-players-online",
      icon: Users,
      label: "Players Online",
      value: stats ? `${stats.players}/${stats.max_players}` : "--",
      sub: stats ? `${stats.queue} in queue` : "",
    },
    {
      testid: "stats-peak-players",
      icon: TrendingUp,
      label: "Peak Today",
      value: stats ? `${stats.peak_today}/${stats?.max_players ?? 64}` : "--",
      sub: "server record pace",
    },
    {
      testid: "stats-uptime",
      icon: Activity,
      label: "Uptime",
      value: stats?.uptime_percent != null ? `${stats.uptime_percent}%` : "--",
      sub: stats?.uptime ? `session ${stats.uptime}` : "awaiting server",
    },
    {
      testid: "stats-ping",
      icon: Gauge,
      label: "Avg Ping",
      value: stats?.ping_ms != null ? `${stats.ping_ms}ms` : "--",
      sub: stats?.ping_ms != null ? "low latency" : "awaiting server",
    },
  ];

  return (
    <div data-testid="live-stats-panel">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <span className="relative flex h-3 w-3">
            <span className={`animate-pulse-dot absolute inline-flex h-full w-full rounded-full ${stats?.status === "offline" ? "bg-red-400" : "bg-emerald-400"}`} />
          </span>
          <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-wide text-cream">
            Live Server Stats
            {stats?.status === "offline" && <span className="ml-3 text-sm text-red-400 align-middle" data-testid="stats-offline-badge">· server offline</span>}
          </h2>
        </div>
        <button
          data-testid="stats-refresh-button"
          onClick={() => fetchStats(true)}
          className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-sunset-amber hover:text-sunset-gold transition-colors"
        >
          <RefreshCw className={`h-4 w-4 ${spinning ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {cards.map((c, i) => (
          <div
            key={c.testid}
            data-testid={c.testid}
            className="glow-card rounded-2xl border border-sunset-amber/20 bg-plum-700/70 p-5 sm:p-6 animate-fade-up"
            style={{ animationDelay: `${i * 90}ms` }}
          >
            <div className="flex items-center justify-between mb-3">
              <c.icon className="h-5 w-5 text-sunset-amber" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{c.label}</span>
            </div>
            <p className="font-mono text-2xl sm:text-3xl font-extrabold text-sunset-gold">{c.value}</p>
            <p className="text-xs text-muted-foreground mt-1">{c.sub}</p>
          </div>
        ))}
      </div>
      {stats && stats.status === "online" && (
        <div className="mt-4 rounded-full h-2 bg-plum-600 overflow-hidden" data-testid="stats-capacity-bar">
          <div
            className="h-full rounded-full bg-gradient-to-r from-sunset-amber to-sunset-orange transition-all duration-700"
            style={{ width: `${(stats.players / stats.max_players) * 100}%` }}
          />
        </div>
      )}
    </div>
  );
}
